import i18n from 'i18next';

import { DataBaseNodes } from '@lib/firebase';
import {
  PATIENT_DEFAULT_BIRTHDATE,
  PATIENT_DEFAULT_NAME,
  PATIENT_DEFAULT_SECRET_ANSWER,
  PATIENT_DEFAULT_SECRET_QUESTION_ID,
} from '@configs';
import {
  DataSourceConfig,
  usePatientStore,
  useSiteStore,
  useWalletStore,
} from '@stores';
import { Site, UUIDTypes } from '@type';
import { getDateUTCOffset } from '@utils';

import { Encryption } from '../../encryption-service';
import { CommunicationKey, CommunicationKeys, Preferences } from '../types';
import { CreateAccountParams } from '../types/auth-gateway.types';
import { BaseGateway } from './base.gateway';
import { SiteGateway } from './site.gateway';
import { GatewayUtils } from './utils';
import { ONBOARDING_TIMEOUT } from './utils/gateway.const';

export class AuthGateway extends BaseGateway {
  private readonly siteGateway: SiteGateway;

  constructor(config: DataSourceConfig) {
    super(config);
    this.siteGateway = new SiteGateway(config);
  }

  exchangeSession = async () => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { patientInfo } = usePatientStore.getState();
    const siteData = getSiteByProjectId(this.config.projectId!);
    const { ipAddress } = await this.utils.getPublicIpInformation();
    const encryptedIpAddress = Encryption.encrypt(
      ipAddress,
      siteData.sharedSecretKey as string
    );

    const payload = {
      preference: Preferences.Session.CreateSession,
      patientUUID: patientInfo.uuid,
      encryptedIpAddress,
    };

    return await this.dataExchange<{
      document: { sessionId: string; userId: number };
    }>({
      siteUuid: siteData.siteUuid,
      payload: payload,
      successKey: CommunicationKeys.Session.Created,
      failureKey: CommunicationKeys.Session.CreationFailed,
    }).catch(error => {
      throw new Error(`Error on exchanging session: ${JSON.stringify(error)}`);
    });
  };

  getSessionValidity = async () => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { patientInfo } = usePatientStore.getState();
    const siteData = getSiteByProjectId(this.config.projectId!);

    const payload = {
      preference: Preferences.Session.SessionValidity,
      sessionId: patientInfo.sessionId,
      sessionLastActive: patientInfo.sessionLastActive,
    };

    const { sessionValidity } = await this.dataExchange<{
      sessionValidity: boolean;
    }>({
      siteUuid: siteData.siteUuid,
      payload: payload,
      successKey: CommunicationKeys.Session.ValiditySuccess,
      failureKey: CommunicationKeys.Session.ValidityFailed,
    }).catch(error => {
      throw new Error(
        `Error on getting session validity: ${JSON.stringify(error)}`
      );
    });
    return sessionValidity;
  };

  endSession = async () => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { patientInfo } = usePatientStore.getState();
    const siteData = getSiteByProjectId(this.config.projectId!);

    const payload = {
      preference: Preferences.Session.EndSession,
      sessionId: patientInfo.sessionId,
    };

    return await this.dataExchange<{ sessionValidity: boolean }>({
      siteUuid: siteData.siteUuid,
      payload: payload,
      successKey: CommunicationKeys.Session.SessionEndingSuccess,
      failureKey: CommunicationKeys.Session.SessionEndingFailure,
    }).catch(error => {
      throw new Error(
        `Error on getting session validity: ${JSON.stringify(error)}`
      );
    });
  };

  validatePatientEmail = async (email: string) => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { sharedSecretKey, siteUuid } = getSiteByProjectId(
      this.config.projectId!
    );

    const payload = {
      firebasePatientId: this.config.patientSourceUUID,
      preference: Preferences.Account.UserEmailExists,
      encryptedEmail: Encryption.encrypt(email, sharedSecretKey),
    };
    return await this.dataExchange({
      siteUuid,
      payload,
      successKey: CommunicationKeys.User.Valid,
      failureKey: CommunicationKeys.User.Invalid,
    });
  };

  createAccount = async ({
    email,
    password,
    firstName,
    lastName,
  }: CreateAccountParams) => {
    const wallet = await useWalletStore
      .getState()
      .getWallet(this.config.patientSourceUUID);

    const { getSiteByProjectId, sites } = useSiteStore.getState();
    const { sharedSecretKey, siteUuid } = getSiteByProjectId(
      this.config.projectId!
    );
    const secretQuestionAnswer = PATIENT_DEFAULT_SECRET_ANSWER;

    const siteList = sites.map(site => ({
      siteUUID: site.siteUuid,
      firebasePatientID: this.config.patientSourceUUID,
      firebaseProjectId: this.config.projectId,
      assetId: site.assetId,
    }));

    const dataToEncrypt = {
      firstName: firstName || PATIENT_DEFAULT_NAME,
      lastName: lastName || PATIENT_DEFAULT_NAME,
      email,
      password,
      ipAddress: (await this.utils.getPublicIpInformation()).ipAddress,
    };

    const encryptedPatientData = Encryption.encrypt(
      dataToEncrypt,
      sharedSecretKey
    );
    const encryptedSecurityAnswer = Encryption.encrypt(
      secretQuestionAnswer,
      sharedSecretKey
    );

    // Encrypt blockchain data
    const encryptedPatientBlockchainIdentifier = Encryption.encryptBySecret(
      secretQuestionAnswer,
      wallet.patientBlockchainIdentifier
    );
    const encryptedPrivateKey = Encryption.encryptBySecret(
      secretQuestionAnswer,
      wallet.privateKey
    );
    const encryptedCertificate = wallet.certificate
      ? Encryption.encryptBySecret(secretQuestionAnswer, wallet.certificate)
      : '';

    await this.sendRequest(
      siteUuid,
      {
        firebasePatientId: this.config.patientSourceUUID,
        preference: Preferences.DummyPatient.AddUUID,
        blockchainKeyId: wallet.patientBlockchainIdentifier,
        patientUuid: wallet.patientUuid,
        patientUuidType: UUIDTypes.PatientUuidType,
      },
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );

    const payload = {
      preference: Preferences.Account.CreateAccount,
      encryptedPatientData,
      patientUUID: wallet.patientUuid,
      securityQuestionId: PATIENT_DEFAULT_SECRET_QUESTION_ID,
      encryptedSecurityAnswer,
      firebasePatientId: this.config.patientSourceUUID,
      blockchainKeyId: encryptedPatientBlockchainIdentifier,
      blockchainCertificates: encryptedCertificate,
      privateKey: encryptedPrivateKey,
      publicKey: wallet.publicKey,
      siteList,
      userLanguage: GatewayUtils.mapLangsToMYCLangs(i18n.language),
      isMigration: false,
    };
    return await this.dataExchange<{
      document: { userId: number; sessionId: string };
    }>({
      siteUuid,
      payload,
      successKey: CommunicationKeys.Account.CreationSuccess,
      failureKey: CommunicationKeys.Account.CreationFailed,
    });
  };

  resendOTP = async () => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { patientInfo } = usePatientStore.getState();
    const site = getSiteByProjectId(this.config.projectId!);

    const payload = {
      preference: Preferences.Account.ResendPin,
      userId: patientInfo.userId,
      sessionId: patientInfo.sessionId,
      userLanguage: GatewayUtils.mapLangsToMYCLangs(i18n.language),
    };

    return await this.sendRequest(
      site.siteUuid,
      payload,
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );
  };

  verifyOTP = async (otp: string, isResend = false) => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { patientInfo } = usePatientStore.getState();
    const site = getSiteByProjectId(this.config.projectId!);

    const payload = {
      preference: Preferences.Account.VerifyOTP,
      userId: patientInfo.userId,
      sessionId: patientInfo.sessionId,
      pin: otp,
      isResendPin: isResend,
    };

    return await this.dataExchange({
      siteUuid: site.siteUuid,
      payload,
      successKey: CommunicationKeys.Account.PinVerified,
      failureKey: [
        CommunicationKeys.Account.PinUnverified,
        CommunicationKeys.Account.PinExpired,
      ],
    });
  };

  initiateClassicOnboarding = async ({
    qrCodeUUID,
    projectId,
    siteUUID,
  }: {
    qrCodeUUID: string;
    projectId: string;
    siteUUID: string;
  }) => {
    const isActive = await this.utils.isQRCodeActive(qrCodeUUID);
    if (!isActive) throw new Error('auth.register.qr.notActive');

    const isUnderMaintenance = await this.sourceService.getMaintenanceMode();
    if (isUnderMaintenance)
      throw new Error('auth.register.qr.underMaintenance');

    const site = await this.siteGateway.getSiteInfoByUuid(siteUUID);

    if (!site) return false;

    const addSite = useSiteStore.getState().addSite;
    addSite(site, projectId);

    const { patientBlockchainIdentifier } = await useWalletStore
      .getState()
      .getWallet();
    const dummyPatient = {
      qrCodeUUID,
      preference: Preferences.DummyPatient.DummyPatient,
      siteUUID: site.siteUuid,
      uuid: this.config.patientSourceUUID,
      deviceOffset: getDateUTCOffset(),
      firebaseKey: this.config.patientSourceUUID,
      patientBlockchainId: patientBlockchainIdentifier,
      patientUuidType: UUIDTypes.PatientUuidType,
      birthdate: PATIENT_DEFAULT_BIRTHDATE,
    };
    const successKeys = GatewayUtils.getOnboardingSuccessKeys(site.siteUuid);
    const { message } = await this.dataExchange<{ message: CommunicationKey }>(
      {
        siteUuid: site.siteUuid,
        payload: dummyPatient,
        query: DataBaseNodes.QrCodeRequest,
        successKey: successKeys,
        failureKey: [
          CommunicationKeys.DummyPatient.Cooldown,
          CommunicationKeys.DummyPatient.ProcessFailure,
          CommunicationKeys.DummyPatient.NonMedicalCountry,
          CommunicationKeys.DummyPatient.Under18,
          CommunicationKeys.DummyPatient.Under18WithoutHandshake,
          CommunicationKeys.DummyPatient.CarepathwayAlreadyAssigned,
          CommunicationKeys.DummyPatient.PatientIssue,
        ],
        pushWithKey: true,
        pushKey: Preferences.DummyPatient.DummyPatient,
      },
      ONBOARDING_TIMEOUT
    );

    return message !== CommunicationKeys.Component.Sent;
  };

  addTrustedSite = async (site: Site) => {
    const payload = {
      firebasePatientID: this.config.patientSourceUUID,
      preference: Preferences.DummyPatient.AddTrustedSite,
      patientUUID: this.config.patientSourceUUID,
      newSiteObject: {
        siteUUID: site.siteUuid,
        firebasePatientID: this.config.patientSourceUUID,
        firebaseProjectId: site.sourceProjectId,
        assetId: site.siteId,
      },
    };
    return await this.sendRequest(
      site.siteUuid,
      payload,
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );
  };

  async removeSiteFromTrustedInstallation(siteUuid: string) {
    const { getSiteByProjectId } = useSiteStore.getState();
    const siteData = getSiteByProjectId(this.config.projectId!);
    const firebasePatientId = this.config.patientSourceUUID;
    const { patientInfo } = usePatientStore.getState();

    const payload = {
      type: Preferences.Careteam.RemoveSiteFromTrustedInstallation,
      preference: Preferences.Careteam.RemoveSiteFromTrustedInstallation,
      firebasePatientId,
      siteUUID: siteUuid,
      patientUUID: patientInfo.uuid,
    };
    return await this.sendRequest(
      siteData.siteUuid,
      payload,
      DataBaseNodes.Request,
      firebasePatientId
    );
  }

  deletePatientAccount = async () => {
    const { patientInfo } = usePatientStore.getState();
    const { getSiteByProjectId } = useSiteStore.getState();
    const site = getSiteByProjectId(this.config.projectId as string);
    const payload = {
      firebasePatientId: this.config.patientSourceUUID,
      siteId: site.siteId,
      preference: Preferences.Account.DeleteAccount,
      patientUUID: patientInfo.uuid,
      userId: patientInfo.userId,
    };
    return await this.sendRequest(
      site.siteUuid,
      payload,
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );
  };
  sendLogoutRequest = async ({
    shouldLogoutFromAllSites,
    siteUuid,
  }: {
    shouldLogoutFromAllSites: boolean;
    siteUuid?: string;
  }) => {
    const { getSiteByProjectId } = useSiteStore.getState();

    const siteUUID = shouldLogoutFromAllSites
      ? getSiteByProjectId(this.config.projectId as string).siteUuid
      : siteUuid;

    if ((!shouldLogoutFromAllSites && !siteUuid) || !siteUUID) return;

    const payload = {
      firebasePatientId: this.config.patientSourceUUID,
      type: Preferences.Account.Logout,
      preference: Preferences.Account.Logout,
      shouldLogoutFromAllSites,
    };

    return await this.sendRequest(
      siteUUID,
      payload,
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );
  };

  checkPatientRegistrationStatus = async () => {
    const { siteUuid } = useSiteStore
      .getState()
      .getSiteByProjectId(this.config.projectId!);
    const payload = {
      firebasePatientId: this.config.patientSourceUUID,
      type: Preferences.Account.CheckRegistrationInformation,
      preference: Preferences.Account.CheckRegistrationInformation,
    };
    const { missingFields } = await this.dataExchange<{
      isComplete?: boolean;
      missingFields: {
        birthday?: boolean;
        gender?: boolean;
        name?: boolean;
      };
    }>({
      successKey: CommunicationKeys.Account.RegistrationStatusSuccess,
      failureKey: CommunicationKeys.Account.RegistrationStatusFailed,
      payload,
      siteUuid,
    });
    return missingFields.birthday && missingFields.name && missingFields.name;
  };
}
